import apiClient from "./apiClient";
import i18n from "../../i18n";

const getAcceptLanguage = () =>
  i18n.language?.includes("ru") ? "ru-RU" : "kk-KZ";

const billingHeaders = () => ({
  "Content-Type": "application/json",
  "Accept-Language": getAcceptLanguage(),
});

const billingExportHeaders = () => ({
  "Accept-Language": getAcceptLanguage(),
  Accept:
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
});

export type AccountStatus = "ACTIVE";

export interface SaveKtAccountRequest {
  ktAccount: string;
}

export interface TopUpAccountRequest {
  amount: number;
  paymentDate: string;
  paymentOrder: string;
  payerData: string;
}

export interface AccountsListFilters {
  orgIinBin?: string;
  productGroupCode?: string;
  status?: AccountStatus;
}

export interface AccountAddress {
  city: string;
  street: string;
  house: string;
  building: string;
  premises: string;
}

export interface AccountBank {
  bankName: string;
  bankIdentificationCode: string;
  checkingAccount: string;
  kbe: string;
}

export interface AccountCard {
  id: number;
  accountNumber: string;
  openedAt: string;
  status: AccountStatus | string;
  ktAccount: string;
  orgId: number;
  orgIinBin: string;
  orgName: string;
  organizationEmail: string;
  address: AccountAddress;
  bank: AccountBank;
  eoContractNumber: string;
  eoContractDate: string;
  productGroupCode: string;
  productGroupName: string;
  initialBalance: number;
  currentBalance: number;
}

export interface AccountOperationsDay {
  date: string;
  balanceIn: number;
  topUpAmount: number;
  paymentOrder: string;
  writeOffAmount: number;
  balanceOut: number;
}

export interface AccountOperationsMonth {
  period: string;
  balanceIn: number;
  topUpAmount: number;
  writeOffAmount: number;
  balanceOut: number;
  days: AccountOperationsDay[];
}

export interface AccountOperationsSummary {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
  accountId: number;
  accountNumber: string;
  dateFrom: string;
  dateTo: string;
  balanceIn: number;
  topUpAmount: number;
  writeOffAmount: number;
  balanceOut: number;
  months: AccountOperationsMonth[];
}

export interface ReconciliationReportRow {
  accountId: number;
  orgName: string;
  orgIinBin: string;
  productGroupCode: string;
  productGroupName: string;
  accountNumber: string;
  marksCount: number;
  topUpAmount: number;
  writeOffAmount: number;
  balanceOut: number;
}

export interface ReconciliationReport {
  dateFrom: string;
  dateTo: string;
  rows: ReconciliationReportRow[];
  totalMarksCount: number;
  totalTopUpAmount: number;
  totalWriteOffAmount: number;
  totalBalanceOut: number;
}

export interface MyActiveAccount {
  accountId: number;
  productGroupCode: string;
  productGroupName: string;
  currentBalance: number;
  updatedAt: string;
  accountNumber: string;
  eoContractNumber: string;
  eoContractDate: string;
}

export const saveKtAccount = async (
  accountId: number,
  payload: SaveKtAccountRequest,
): Promise<any> => {
  try {
    const response = await apiClient.put(
      `${process.env.REACT_APP_URL_BILLING_SERVICE}/accounts/${accountId}/kt-account`,
      payload,
      {
        headers: billingHeaders(),
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error(`Error saving KT account for account ${accountId}:`, error);
    throw error;
  }
};

export const topUpAccount = async (
  accountId: number,
  payload: TopUpAccountRequest,
): Promise<any> => {
  try {
    const response = await apiClient.post(
      `${process.env.REACT_APP_URL_BILLING_SERVICE}/accounts/${accountId}/top-up`,
      payload,
      {
        headers: billingHeaders(),
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error(`Error topping up account ${accountId}:`, error);
    throw error;
  }
};

export const getAccountsList = async (
  page: number = 1,
  size: number = 10,
  filters?: AccountsListFilters,
): Promise<any> => {
  try {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
    });

    if (filters?.orgIinBin) {
      params.set("orgIinBin", filters.orgIinBin);
    }
    if (filters?.productGroupCode) {
      params.set("productGroupCode", filters.productGroupCode);
    }
    if (filters?.status) {
      params.set("status", filters.status);
    }

    const response = await apiClient.get(
      `${process.env.REACT_APP_URL_BILLING_SERVICE}/accounts?${params.toString()}`,
      {
        headers: billingHeaders(),
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching accounts list:", error);
    throw error;
  }
};

export const getAccountById = async (
  accountId: number,
): Promise<AccountCard> => {
  try {
    const response = await apiClient.get(
      `${process.env.REACT_APP_URL_BILLING_SERVICE}/accounts/${accountId}`,
      {
        headers: billingHeaders(),
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching account ${accountId}:`, error);
    throw error;
  }
};

export const getAccountOperationsSummary = async (
  accountId: number,
  dateFrom?: string,
  dateTo?: string,
  page: number = 1,
  size: number = 10,
): Promise<AccountOperationsSummary> => {
  try {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
    });

    if (dateFrom) {
      params.set("dateFrom", dateFrom);
    }
    if (dateTo) {
      params.set("dateTo", dateTo);
    }

    const response = await apiClient.get(
      `${process.env.REACT_APP_URL_BILLING_SERVICE}/accounts/${accountId}/operations/summary?${params.toString()}`,
      {
        headers: billingHeaders(),
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching operations summary for account ${accountId}:`,
      error,
    );
    throw error;
  }
};

export const exportAccountOperationsSummary = async (
  accountId: number,
  dateFrom?: string,
  dateTo?: string,
): Promise<Blob> => {
  try {
    const params = new URLSearchParams();

    if (dateFrom) {
      params.set("dateFrom", dateFrom);
    }
    if (dateTo) {
      params.set("dateTo", dateTo);
    }

    const query = params.toString();
    const response = await apiClient.get(
      `${process.env.REACT_APP_URL_BILLING_SERVICE}/accounts/${accountId}/operations/summary/export${query ? `?${query}` : ""}`,
      {
        headers: billingExportHeaders(),
        withCredentials: true,
        responseType: "blob",
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error exporting operations summary for account ${accountId}:`,
      error,
    );
    throw error;
  }
};

export const getMyActiveAccounts = async (): Promise<MyActiveAccount[]> => {
  try {
    const response = await apiClient.get(
      `${process.env.REACT_APP_URL_BILLING_SERVICE}/accounts/my/active`,
      {
        headers: billingHeaders(),
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching active accounts:", error);
    throw error;
  }
};

export const getReconciliationReport = async (
  dateFrom: string,
  dateTo: string,
): Promise<ReconciliationReport> => {
  try {
    const params = new URLSearchParams({
      dateFrom,
      dateTo,
    });

    const response = await apiClient.get(
      `${process.env.REACT_APP_URL_BILLING_SERVICE}/reports/reconciliation?${params.toString()}`,
      {
        headers: billingHeaders(),
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching reconciliation report:", error);
    throw error;
  }
};

export const exportReconciliationReport = async (
  dateFrom: string,
  dateTo: string,
): Promise<Blob> => {
  try {
    const params = new URLSearchParams({
      dateFrom,
      dateTo,
    });

    const response = await apiClient.get(
      `${process.env.REACT_APP_URL_BILLING_SERVICE}/reports/reconciliation/export?${params.toString()}`,
      {
        headers: billingExportHeaders(),
        withCredentials: true,
        responseType: "blob",
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error exporting reconciliation report:", error);
    throw error;
  }
};
